import React, { useState } from 'react';
import Button from './ui/Button';
import GoogleButton from './ui/GoogleButton';
import { Search, MapPin, Loader2, Lock, CheckCircle2, ShieldCheck, Mail, Phone } from 'lucide-react';

type SearchState = 'idle' | 'loading' | 'locked' | 'unlocked';

interface PropertyRecord {
  id: string;
  license_number: string;
  name: string;
  email?: string;
  phone?: string;
  status: string;
  state: string;
  city?: string;
  brokerage?: string;
  license_issued: string;
  license_expires: string;
}

const LeadMagnetSearch: React.FC = () => {
  const [state, setState] = useState<SearchState>('idle');
  const [zip, setZip] = useState('');
  const [profession, setProfession] = useState('Real Estate Agent');
  const [searchResults, setSearchResults] = useState<PropertyRecord[]>([]);
  const [resultsCount, setResultsCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const credits = 3;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zip) return;

    setState('loading');
    setError(null);

    try {
      // Use live-search endpoint for real-time scraping
      const response = await fetch('/api/live-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          zip,
          profession: profession.toLowerCase().replace(' ', '_'),
          limit: 10,
          radius: 25, // Search within 25 miles
        }),
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json() as any;
      setSearchResults(data.results || []);
      setResultsCount(data.count || 0);
      setState('locked');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Search failed';
      setError(message);
      setState('idle');
    }
  };

  const handleUnlock = async () => {
    setState('loading');
    // In production, this would authenticate with Google OAuth
    // For now, simulate successful auth
    setTimeout(() => {
      setState('unlocked');
    }, 1000);
  };

  const handleBuyCredits = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-900/90 backdrop-blur-xl border border-slate-700 rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
      {/* HEADER */}
      <div className="bg-slate-800/50 p-4 border-b border-slate-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></div>
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Live Database</span>
        </div>
        {state === 'unlocked' && (
          <div className="text-xs font-bold text-sky-400 bg-sky-900/30 px-3 py-1 rounded-full border border-sky-500/30">
            {credits} Free Credits Remaining
          </div>
        )}
      </div>

      {/* SEARCH FORM (Visible in idle/loading, hidden in locked/unlocked if desired, or kept) */}
      {(state === 'idle' || state === 'loading') && (
        <div className="p-8">
          <h3 className="text-2xl font-bold text-white mb-2 text-center">Find Local Professionals</h3>
          <p className="text-slate-400 text-center mb-8">Enter a location to verify active licenses and contact info.</p>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <select
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-lg px-4 py-3 focus:ring-2 focus:ring-sky-500 focus:border-transparent appearance-none"
                  value={profession}
                  onChange={(e) => setProfession(e.target.value)}
                >
                  <option>Real Estate Agent</option>
                  <option>Insurance Agent</option>
                  <option>Dentist</option>
                  <option>Attorney</option>
                  <option>General Contractor</option>
                </select>
                <div className="absolute right-3 top-3.5 pointer-events-none text-slate-500">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>

              <div className="relative">
                <MapPin className="absolute left-3 top-3.5 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  placeholder="Zip Code (e.g. 90210)"
                  className="w-full bg-slate-950 border border-slate-700 text-white rounded-lg pl-10 pr-4 py-3 focus:ring-2 focus:ring-sky-500 focus:border-transparent placeholder:text-slate-600"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                />
              </div>
            </div>

            <Button
              className="w-full"
              size="lg"
              disabled={state === 'loading'}
            >
              {state === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Searching Registry...
                </>
              ) : (
                <>
                  <Search className="w-5 h-5 mr-2" />
                  Search Records
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 flex justify-center gap-6 text-xs text-slate-500">
            <span className="flex items-center"><ShieldCheck className="w-3 h-3 mr-1" /> Official State Data</span>
            <span className="flex items-center"><CheckCircle2 className="w-3 h-3 mr-1" /> Daily Verified</span>
          </div>
        </div>
      )}

      {/* LOCKED RESULTS VIEW */}
      {state === 'locked' && (
        <div className="relative">
          <div className="p-6 bg-slate-900">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-lg">Found {resultsCount} Results in {zip}</h3>
              <button onClick={() => setState('idle')} className="text-sm text-slate-400 hover:text-white">New Search</button>
            </div>

            <div className="space-y-3">
              {/* Visible Item 1 - Show first result from API */}
              {searchResults.length > 0 && (
                <div className="bg-slate-800 p-4 rounded-lg border border-slate-700 flex justify-between items-start">
                  <div>
                    <div className="font-bold text-white text-lg">{searchResults[0].name}</div>
                    <div className="text-slate-400 text-sm">{searchResults[0].brokerage || 'Real Estate Professional'}</div>
                    <div className="text-xs text-slate-500 mt-1">Lic #{searchResults[0].license_number}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                      {searchResults[0].status}
                    </span>
                    <div className="h-8 w-24 bg-slate-700 rounded animate-pulse"></div>
                  </div>
                </div>
              )}

              {/* Blurred Items - Show next 3 results */}
              {searchResults.slice(1, 4).map((item) => (
                <div key={item.id} className="bg-slate-800 p-4 rounded-lg border border-slate-700 flex justify-between items-start opacity-50 blur-[2px]">
                  <div>
                    <div className="font-bold text-white text-lg">Hidden Record</div>
                    <div className="text-slate-400 text-sm">{item.brokerage || 'Real Estate Professional'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* LOCK OVERLAY */}
          <div className="absolute inset-0 top-20 bg-gradient-to-b from-slate-900/60 via-slate-900/95 to-slate-900 backdrop-blur-[1px] flex flex-col items-center justify-center p-8 text-center z-10">
            <div className="bg-slate-800 p-4 rounded-full mb-4 shadow-xl border border-slate-700">
              <Lock className="w-8 h-8 text-sky-500" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Unlock Full Contact Data</h3>
            <p className="text-slate-400 mb-8 max-w-sm">
              Create a free account to view phone numbers, email addresses, and license details for these professionals.
            </p>

            <div className="w-full max-w-xs space-y-4">
              <GoogleButton onClick={handleUnlock} className="w-full" />
              <p className="text-xs text-slate-500">
                By continuing, you agree to our Terms of Service.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* UNLOCKED RESULTS VIEW */}
      {state === 'unlocked' && (
        <div className="p-6 bg-slate-900 max-h-[500px] overflow-y-auto custom-scrollbar">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h3 className="text-white font-bold text-lg">Results for {zip}</h3>
              <p className="text-xs text-slate-400">Showing {searchResults.length} of {resultsCount} records</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setState('idle')}>New Search</Button>
          </div>

          <div className="space-y-4 mb-8">
            {searchResults.map((item) => (
              <div key={item.id} className="bg-slate-800 p-4 rounded-lg border border-slate-700 transition-colors hover:border-slate-600">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="font-bold text-white text-lg flex items-center gap-2">
                      {item.name}
                      <ShieldCheck className="w-4 h-4 text-sky-500" />
                    </div>
                    <div className="text-slate-400 text-sm">{item.brokerage || 'Real Estate Professional'}</div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    {item.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-700/50">
                  <div className="flex items-center text-sm text-slate-300">
                    <Phone className="w-4 h-4 mr-2 text-slate-500" />
                    {item.phone || 'Not available'}
                  </div>
                  <div className="flex items-center text-sm text-slate-300">
                    <Mail className="w-4 h-4 mr-2 text-slate-500" />
                    {item.email || 'Not available'}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* UPSELL CARD */}
          <div className="bg-gradient-to-r from-sky-900/40 to-indigo-900/40 border border-sky-500/30 rounded-xl p-6 text-center">
            <h4 className="text-white font-bold mb-2">Need more data?</h4>
            <p className="text-sm text-slate-400 mb-6">
              You are viewing a limited preview. Get full access to millions of records, bulk export, and API access.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button variant="primary" size="sm" onClick={handleBuyCredits}>
                Buy Credits ($5)
              </Button>
              <Button variant="secondary" size="sm" onClick={handleBuyCredits}>
                View Plans
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadMagnetSearch;